import React, { ReactNode, useState } from 'react';

import {
  Box,
  Collapse,
  Grid,
  GridProps,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';

import { ChevronRight } from '../atoms/icons/chevron-right';
import { useVisibility, VisibilityProps } from '../store/use-visibility';

type Props = {
  type?: 'column' | 'row';
  icon?: ReactNode;
  title?: ReactNode;
  value?: ReactNode;
  gridItem?: GridProps;
  expanded?: ReactNode;
} & VisibilityProps;

export const ValueBox = ({
  type = 'column',
  icon,
  title,
  value,
  gridItem,
  expanded,
  visibility,
}: Props) => {
  const isExpandable = Boolean(expanded);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!useVisibility(visibility)) return null;

  const box = (
    <Paper
      sx={{
        height: type === 'column' ? 150 : 'auto',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: type === 'column' ? 'column' : 'row',
          alignItems: type === 'column' ? 'flex-start' : 'center',
          height: '100%',
          width: '100%',
        }}
      >
        {icon && (
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'common.white',
              width: type === 'column' ? 32 : 40,
              height: type === 'column' ? 32 : 40,
              '& .MuiSvgIcon-root': {
                fontSize: type === 'column' ? 24 : 32,
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: type === 'column' ? 0 : 3,
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ mt: type === 'column' ? 'auto' : 0 }}>
          <Typography
            variant="h3"
            sx={{
              maxWidth: type === 'column' ? 100 : '100%',
              mb: type === 'column' ? 0.5 : 0.25,
            }}
          >
            {title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.disabled' }}>
            {value}
          </Typography>
          <Collapse in={isExpandable && isExpanded}>{expanded}</Collapse>
        </Box>

        {isExpandable && (
          <>
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ ml: 'auto' }}
            >
              <ChevronRight
                sx={{
                  transform: `rotate(${isExpanded ? '-' : ''}90deg)`,
                  transition: 'ease-in-out 200ms transform',
                }}
              />
            </IconButton>
          </>
        )}
      </Box>
    </Paper>
  );

  if (gridItem) {
    return <Grid {...gridItem}>{box}</Grid>;
  }

  return box;
};
